import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const AIUsageLogs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [viewMode, setViewMode] = useState('logs'); // 'logs' or 'summary'

  // Date range state
  const [dateRangeType, setDateRangeType] = useState('this_month'); // 'this_month', 'previous_month', 'ytd', 'custom', 'all'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Calculate date range based on selection
  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate = null;
    let endDate = null;

    switch (dateRangeType) {
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case 'previous_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'ytd':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = now;
        break;
      case 'custom':
        if (customStartDate) startDate = new Date(customStartDate);
        if (customEndDate) {
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59);
        }
        break;
      case 'all':
      default:
        // No date filter
        break;
    }

    return { startDate, endDate };
  }, [dateRangeType, customStartDate, customEndDate]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
      });
      if (organizationFilter) {
        params.append('organization_id', organizationFilter);
      }

      const { startDate, endDate } = getDateRange();
      if (startDate) {
        params.append('start_date', startDate.toISOString());
      }
      if (endDate) {
        params.append('end_date', endDate.toISOString());
      }

      const response = await axios.get(
        `${apiBase}/admin/ai-usage-logs?${params}`,
        { headers }
      );

      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching AI usage logs:', error);
    } finally {
      setLoading(false);
    }
  }, [skip, limit, organizationFilter, getDateRange]);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const params = new URLSearchParams();
      if (organizationFilter) {
        params.append('organization_id', organizationFilter);
      }

      const { startDate, endDate } = getDateRange();
      if (startDate) {
        params.append('start_date', startDate.toISOString());
      }
      if (endDate) {
        params.append('end_date', endDate.toISOString());
      }

      const response = await axios.get(
        `${apiBase}/admin/ai-usage-summary?${params}`,
        { headers }
      );

      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching AI usage summary:', error);
    } finally {
      setLoading(false);
    }
  }, [organizationFilter, getDateRange]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/admin/organizations`, { headers });
      setOrganizations(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchOrganizations();
    }
  }, [user, fetchOrganizations]);

  useEffect(() => {
    if (user?.role === 'superadmin') {
      if (viewMode === 'logs') {
        fetchLogs();
      } else {
        fetchSummary();
      }
    }
  }, [user, viewMode, fetchLogs, fetchSummary]);

  // Compute global statistics by model from summary data
  const globalModelStats = useMemo(() => {
    if (!summary?.by_organization) return [];

    const modelMap = new Map();

    summary.by_organization.forEach(org => {
      org.by_model?.forEach(model => {
        const key = `${model.provider}|${model.model}`;
        const existing = modelMap.get(key);
        if (existing) {
          existing.input_tokens += model.input_tokens || 0;
          existing.output_tokens += model.output_tokens || 0;
          existing.total_tokens += model.total_tokens || 0;
          existing.cost_usd += model.cost_usd || 0;
          existing.components += model.components || 0;
        } else {
          modelMap.set(key, {
            provider: model.provider,
            model: model.model,
            input_tokens: model.input_tokens || 0,
            output_tokens: model.output_tokens || 0,
            total_tokens: model.total_tokens || 0,
            cost_usd: model.cost_usd || 0,
            components: model.components || 0
          });
        }
      });
    });

    return Array.from(modelMap.values()).sort((a, b) => b.cost_usd - a.cost_usd);
  }, [summary]);

  const toggleLogExpansion = (logId) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatDateRange = () => {
    const { startDate, endDate } = getDateRange();
    if (!startDate && !endDate) return 'All Time';

    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    const start = startDate ? startDate.toLocaleDateString('en-US', options) : '';
    const end = endDate ? endDate.toLocaleDateString('en-US', options) : '';

    if (start && end) return `${start} - ${end}`;
    if (start) return `From ${start}`;
    if (end) return `Until ${end}`;
    return 'All Time';
  };

  const getProviderColor = (provider) => {
    if (provider === 'claude') {
      return 'bg-orange-100 text-orange-800';
    } else if (provider === 'gemini') {
      return 'bg-blue-100 text-blue-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const getComponentTypeColor = (type) => {
    const colors = {
      'commercial_flight': 'bg-sky-100 text-sky-800',
      'private_flight': 'bg-violet-100 text-violet-800',
      'hotel': 'bg-emerald-100 text-emerald-800',
      'car_rental': 'bg-amber-100 text-amber-800',
      'restaurant': 'bg-rose-100 text-rose-800',
      'chauffeur_service': 'bg-indigo-100 text-indigo-800',
      'email_processing_misc': 'bg-gray-200 text-gray-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatComponentType = (type) => {
    const labels = {
      'commercial_flight': 'Flight',
      'private_flight': 'Private Flight',
      'hotel': 'Hotel',
      'car_rental': 'Car Rental',
      'restaurant': 'Restaurant',
      'chauffeur_service': 'Chauffeur',
      'email_processing_misc': 'Misc (No Component)',
    };
    return labels[type] || type;
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          Access denied. Superadmin privileges required.
        </div>
      </div>
    );
  }

  if (loading && logs.length === 0 && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Usage Logs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor AI model usage and costs per component
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-gray-300">
            <button
              onClick={() => setViewMode('logs')}
              className={`px-4 py-2 text-sm font-medium ${
                viewMode === 'logs'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Logs
            </button>
            <button
              onClick={() => setViewMode('summary')}
              className={`px-4 py-2 text-sm font-medium ${
                viewMode === 'summary'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Summary
            </button>
          </div>

          {/* Organization Filter */}
          <select
            value={organizationFilter}
            onChange={(e) => {
              setOrganizationFilter(e.target.value);
              setSkip(0);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Organizations</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>

          {viewMode === 'logs' && (
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setSkip(0);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={25}>Show 25</option>
              <option value={50}>Show 50</option>
              <option value={100}>Show 100</option>
            </select>
          )}
        </div>
      </div>

      {/* Date Range Filter - Only for Summary view */}
      {viewMode === 'summary' && (
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Date Range:</span>

            {/* Preset buttons */}
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              {[
                { value: 'this_month', label: 'This Month' },
                { value: 'previous_month', label: 'Previous Month' },
                { value: 'ytd', label: 'Year to Date' },
                { value: 'all', label: 'All Time' },
                { value: 'custom', label: 'Custom' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setDateRangeType(option.value)}
                  className={`px-3 py-1.5 text-sm font-medium border-r last:border-r-0 ${
                    dateRangeType === option.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Custom date inputs */}
            {dateRangeType === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                />
              </div>
            )}

            {/* Display current range */}
            <span className="text-sm text-gray-500 ml-auto">
              Showing: <span className="font-medium">{formatDateRange()}</span>
            </span>
          </div>
        </div>
      )}

      {viewMode === 'summary' && summary && (
        <>
          {/* Grand Totals */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Totals</h2>
            <div className="grid grid-cols-5 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500">Organizations</div>
                <div className="text-2xl font-bold text-gray-900">
                  {summary.totals.organization_count}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500">Components</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(summary.totals.total_components)}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500">Input Tokens</div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatNumber(summary.totals.total_input_tokens)}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500">Output Tokens</div>
                <div className="text-2xl font-bold text-green-600">
                  {formatNumber(summary.totals.total_output_tokens)}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500">Total Cost</div>
                <div className="text-2xl font-bold text-orange-600">
                  {formatCurrency(summary.totals.total_cost_usd)}
                </div>
              </div>
            </div>
          </div>

          {/* Statistics by Model - Global */}
          {globalModelStats.length > 0 && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Statistics by Model</h2>
                <p className="text-sm text-gray-500">Usage breakdown across all organizations</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Components</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Input Tokens</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Output Tokens</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {globalModelStats.map((model, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getProviderColor(model.provider)}`}>
                            {model.provider}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {model.model}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatNumber(model.components)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 text-right">
                          {formatNumber(model.input_tokens)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-right">
                          {formatNumber(model.output_tokens)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600 text-right">
                          {formatCurrency(model.cost_usd)}
                        </td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" colSpan={2}>
                        Total
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatNumber(globalModelStats.reduce((sum, m) => sum + m.components, 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 text-right">
                        {formatNumber(globalModelStats.reduce((sum, m) => sum + m.input_tokens, 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-right">
                        {formatNumber(globalModelStats.reduce((sum, m) => sum + m.output_tokens, 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-orange-600 text-right">
                        {formatCurrency(globalModelStats.reduce((sum, m) => sum + m.cost_usd, 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Per Organization */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Usage by Organization</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {summary.by_organization.map((org) => (
                <div key={org.organization_id} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {org.organization_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {formatNumber(org.total_components)} components processed
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-orange-600">
                        {formatCurrency(org.total_cost_usd)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatNumber(org.total_tokens)} total tokens
                      </div>
                    </div>
                  </div>

                  {/* Model breakdown */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">By Model</h4>
                    <div className="space-y-2">
                      {org.by_model.map((model, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getProviderColor(model.provider)}`}>
                              {model.provider}
                            </span>
                            <span className="text-gray-700">{model.model}</span>
                          </div>
                          <div className="flex items-center gap-4 text-gray-600">
                            <span>{formatNumber(model.components)} components</span>
                            <span className="text-blue-600">{formatNumber(model.input_tokens)} in</span>
                            <span className="text-green-600">{formatNumber(model.output_tokens)} out</span>
                            <span className="font-medium text-gray-900">
                              {formatCurrency(model.cost_usd)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {summary.by_organization.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  No AI usage data available for the selected date range
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {viewMode === 'logs' && (
        <>
          {/* Stats */}
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{logs.length}</span> of{' '}
                <span className="font-semibold">{total}</span> total logs
              </div>
              {total > limit && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={skip === 0}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setSkip(skip + limit)}
                    disabled={skip + limit >= total}
                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Logs List */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {logs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="text-lg">No AI usage logs yet</p>
                <p className="text-sm mt-2">AI usage will be recorded when emails are parsed into components</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <div key={log.id} className="p-4 hover:bg-gray-50">
                    <div
                      className="cursor-pointer"
                      onClick={() => toggleLogExpansion(log.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            {/* Show badges for each unique model/provider used */}
                            {log.cost_by_model && Object.keys(log.cost_by_model).length > 0 ? (
                              Object.keys(log.cost_by_model).map((model) => (
                                <span key={model} className={`px-2 py-1 rounded text-xs font-medium ${getProviderColor(model.startsWith('claude') ? 'claude' : 'gemini')}`}>
                                  {model}
                                </span>
                              ))
                            ) : (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getProviderColor(log.ai_provider)}`}>
                                {log.ai_model || log.ai_provider}
                              </span>
                            )}
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getComponentTypeColor(log.component_type)}`}>
                              {formatComponentType(log.component_type)}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            {log.component_title || `Component ${log.component_id.slice(0, 8)}...`}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>{formatDate(log.created_at)}</span>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0 text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {formatNumber(log.total_tokens)} tokens
                          </div>
                          <div className="text-sm text-orange-600 font-medium">
                            {formatCurrency(log.total_cost_usd)}
                          </div>
                          <svg
                            className={`h-5 w-5 text-gray-400 transform transition-transform inline-block ml-2 ${
                              expandedLogId === log.id ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {expandedLogId === log.id && (
                      <div className="mt-4 border-t border-gray-200 pt-4">
                        {/* Cost Breakdown by Model */}
                        <div className="mb-4">
                          <div className="flex justify-between items-start">
                            <h5 className="font-semibold text-sm text-gray-700 mb-2">Cost Breakdown by Model</h5>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">Total Cost</div>
                              <div className="text-lg font-bold text-orange-600">{formatCurrency(log.total_cost_usd)}</div>
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            {log.cost_by_model && Object.keys(log.cost_by_model).length > 0 ? (
                              <div className="space-y-2">
                                {Object.entries(log.cost_by_model).map(([model, costs]) => (
                                  <div key={model} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getProviderColor(model.startsWith('claude') ? 'claude' : 'gemini')}`}>
                                        {model.startsWith('claude') ? 'claude' : 'gemini'}
                                      </span>
                                      <span className="text-sm text-gray-700">{model}</span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                      <span className="text-blue-600">{formatNumber(costs.input_tokens || 0)} in</span>
                                      <span className="text-green-600">{formatNumber(costs.output_tokens || 0)} out</span>
                                      <span className="font-semibold text-gray-900">{formatCurrency(costs.total_cost_usd || 0)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Input Tokens</div>
                                  <div className="text-lg font-semibold text-blue-600">
                                    {formatNumber(log.total_input_tokens)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatCurrency(log.input_cost_usd || 0)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Output Tokens</div>
                                  <div className="text-lg font-semibold text-green-600">
                                    {formatNumber(log.total_output_tokens)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatCurrency(log.output_cost_usd || 0)}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* API Calls Breakdown */}
                        {log.calls && log.calls.length > 0 && (
                          <div className="mb-4">
                            <h5 className="font-semibold text-sm text-gray-700 mb-2">API Calls ({log.calls.length})</h5>
                            <div className="bg-gray-50 rounded overflow-hidden">
                              <table className="min-w-full text-xs">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-gray-600">Type</th>
                                    <th className="px-3 py-2 text-left text-gray-600">Model</th>
                                    <th className="px-3 py-2 text-right text-gray-600">Input</th>
                                    <th className="px-3 py-2 text-right text-gray-600">Output</th>
                                    <th className="px-3 py-2 text-right text-gray-600">Cost</th>
                                    <th className="px-3 py-2 text-right text-gray-600">Time</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {log.calls.map((call, idx) => (
                                    <tr key={idx}>
                                      <td className="px-3 py-2 text-gray-900">{call.call_type}</td>
                                      <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${getProviderColor(call.model?.startsWith('claude') ? 'claude' : 'gemini')}`}>
                                          {call.model}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-right text-blue-600">{formatNumber(call.input_tokens)}</td>
                                      <td className="px-3 py-2 text-right text-green-600">{formatNumber(call.output_tokens)}</td>
                                      <td className="px-3 py-2 text-right font-medium text-orange-600">
                                        {formatCurrency(call.cost_usd || 0)}
                                      </td>
                                      <td className="px-3 py-2 text-right text-gray-500">
                                        {formatDate(call.timestamp)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Metadata */}
                        <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                          <div>
                            <span className="font-medium">Component ID:</span> {log.component_id}
                          </div>
                          <div>
                            <span className="font-medium">Organization:</span> {log.organization_id}
                          </div>
                          <div>
                            <span className="font-medium">Source:</span> {log.source_type}
                          </div>
                          {log.source_id && (
                            <div>
                              <span className="font-medium">Source ID:</span> {log.source_id}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AIUsageLogs;
